/* eslint-disable lit/no-invalid-html, lit/binding-positions */
import { type PropertyValues, unsafeCSS } from 'lit';
import { property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { html, literal } from 'lit/static-html.js';
import { BaseElement, customElement, watch } from '../../base-element';
import { Translate } from '../../base-element/mixins/translation-mixin';
import '../../icon/icon.wc';
import styles from './menu-item.scss?inline';

@customElement('ps-menu-item')
export class MenuItemWC extends Translate(BaseElement) {
  static styles = unsafeCSS(styles);

  private cachedTextLabel: string;

  @query('slot:not([name])') defaultSlot: HTMLSlotElement;

  /** Set the item in a selected state. */
  @property({ type: Boolean, reflect: true }) selected = false;

  /**
   * When true, `selected` is a check state: the host role becomes
   * `menuitemradio` and `aria-checked` follows `selected`. Items that only
   * paint a check icon leave this off, so their role stays `menuitem`.
   */
  @property({ type: Boolean }) checkable = false;

  /** Host attribute `test-id`, set only when non-empty. */
  @property({ attribute: 'test-id' }) testId = '';

  /** A unique value to store in the menu item. This can be used as a way to identify menu items when selected. */
  @property() value = '';

  /** Set the menu item in a disabled state, prevents selection. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Marking a MenuItem as "subtle" disables visible interactive states. @deprecated */
  @property({ type: Boolean, reflect: true })
  set subtle(val: boolean) {
    if (val) this.variant = 'subtle';
  }

  /** Set menu-item variant. */
  @property({ reflect: true }) variant:
    | 'default'
    | 'subtle'
    | 'ghost'
    | 'filled'
    | 'danger' = 'default';

  /**
   * When set, the item renders its row box as a real `<a href>` instead of a
   * `<div>`, so the browser resolves native middle-click / right-click "Open
   * link in new tab" by hit-testing the anchor box. The `ps-menu-item` host
   * stays the DIRECT slotted child of `ps-menu`, so keyboard roving
   * (`getAllItems`) is unaffected — this is the first-class alternative to
   * hand-wrapping a `<MenuItem>` in a light-DOM `<a>` (PSTUDIO-3773).
   */
  @property() href?: HTMLAnchorElement['href'];

  /** Anchor `target`, applied only when the item is a link (`href` set). */
  @property({ reflect: true }) target?: HTMLAnchorElement['target'];

  /** Anchor `rel`, applied only when the item is a link (`href` set). */
  @property({ reflect: true }) rel?: HTMLAnchorElement['rel'];

  @query('.c-menu-item') private menuItemBox?: HTMLElement;

  private isLink() {
    return !!this.href;
  }

  /**
   * `ps-menu` activates Enter via `getCurrentItem().click()` — a programmatic
   * click on the HOST (roving keeps focus on the host, not the inner anchor).
   * For a link item, forward that to the real inner `<a>` so native navigation
   * fires; a consumer's own bubbled `click` handler can still `preventDefault`
   * to run an SPA navigation instead. Non-link items keep the default host
   * click dispatch, and a disabled item defers to the host's disabled guard.
   */
  click() {
    if (this.isLink() && !this.disabled && this.menuItemBox) {
      (this.menuItemBox as HTMLAnchorElement).click();
      return;
    }
    super.click();
  }

  connectedCallback() {
    // eslint-disable-next-line wc/guard-super-call
    super.connectedCallback();
    this.setAttribute('role', 'menuitem');
    // A dropdown moves this node into its panel, which reconnects it.
    // updated() does not run again, so the line above would leave a checkable
    // item as role=menuitem with aria-checked still set. Restore the check role
    // when checkable is already true.
    this.syncCheckableAria();
    this.handleHostClick = this.handleHostClick.bind(this);
    this.addEventListener('click', this.handleHostClick);
    // A real middle-click fires `auxclick` (not `click`), so guard it with the
    // same disabled contract — otherwise a disabled link item still renders as
    // an `<a>` (no `pointer-events: none`) and a middle-click would leak an
    // `auxclick` to a consumer's `onAuxClick` even though the primary click is
    // suppressed.
    this.addEventListener('auxclick', this.handleHostClick);
  }

  disconnectedCallback() {
    // eslint-disable-next-line wc/guard-super-call
    super.disconnectedCallback();
    this.removeEventListener('click', this.handleHostClick);
    this.removeEventListener('auxclick', this.handleHostClick);
  }

  private handleHostClick(event: MouseEvent) {
    // Suppress click / auxclick when disabled, so no navigation or consumer
    // click/auxclick handler fires for a disabled item.
    if (this.disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  @watch('disabled')
  handleDisabledChange() {
    this.setAttribute('aria-disabled', this.disabled ? 'true' : 'false');
  }

  protected override updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties);
    if (
      changedProperties.has('checkable') ||
      changedProperties.has('selected')
    ) {
      this.syncCheckableAria();
    }
    if (changedProperties.has('testId')) {
      if (this.testId) {
        if (this.getAttribute('test-id') !== this.testId) {
          this.setAttribute('test-id', this.testId);
        }
      } else if (this.hasAttribute('test-id')) {
        this.removeAttribute('test-id');
      }
    }
  }

  private syncCheckableAria() {
    if (!this.checkable) {
      if (this.getAttribute('role') === 'menuitemradio') {
        this.setAttribute('role', 'menuitem');
      }
      this.removeAttribute('aria-checked');
      return;
    }
    this.setAttribute('role', 'menuitemradio');
    this.setAttribute('aria-checked', this.selected ? 'true' : 'false');
  }

  private handleDefaultSlotChange() {
    const textLabel = this.getTextLabel();

    // Ignore the first time the label is set
    if (typeof this.cachedTextLabel === 'undefined') {
      this.cachedTextLabel = textLabel;
      return;
    }

    // When the label changes, emit a slotchange event so parent controls see it
    if (textLabel !== this.cachedTextLabel) {
      this.cachedTextLabel = textLabel;
      this.emit('slotchange', {
        bubbles: true,
        composed: false,
        cancelable: false,
      });
    }
  }

  getTextLabel() {
    if (!this.defaultSlot) {
      return '';
    }
    const nodes = this.defaultSlot.assignedNodes({ flatten: true });
    let text = '';

    [...nodes].forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent;
      }
    });

    return text;
  }

  render() {
    const isLink = this.isLink();
    const tag = isLink ? literal`a` : literal`div`;
    // Drop href while disabled so a disabled link is inert (not focusable, not
    // navigable) — mirrors the host's disabled click guard.
    const linkHref = isLink && !this.disabled ? this.href : undefined;

    return html`
      <${tag}
        data-testid="menu-item"
        class=${classMap({
          'c-menu-item': true,
          'c-menu-item--disabled': this.disabled,
          'c-menu-item--selected': this.selected,
          [`c-menu-item--${this.variant}`]: this.variant,
        })}
        href=${ifDefined(linkHref)}
        target=${ifDefined(isLink ? this.target : undefined)}
        rel=${ifDefined(isLink ? this.rel : undefined)}
        tabindex=${ifDefined(isLink ? '-1' : undefined)}
      >
        ${
          this.slotted('prefix')
            ? html`<slot
                name="prefix"
                class="c-menu-item__prefix"
                data-testid="menu-item-prefix"
              >
              </slot>`
            : null
        }
        <span class="c-menu-item__content" data-testid="menu-item-content">
          <slot @slotchange=${this.handleDefaultSlotChange}></slot>
          <span class="c-menu-item__secondary"
            ><slot name="secondary"></slot
          ></span>
        </span>
        ${
          this.slotted('suffix') && !this.selected
            ? html`<slot
                name="suffix"
                class="c-menu-item__suffix"
                data-testid="menu-item-suffix"
              >
              </slot>`
            : null
        }
        ${
          this.selected
            ? html`<ps-icon
                name="check"
                size="xsmall"
                weight="bold"
                class="c-menu-item__check-icon"
                data-testid="menu-item-check-icon"
              ></ps-icon>`
            : null
        }
      </${tag}>
    `;
  }
}

export type MenuItemSelectedEvent = CustomEvent<{ item: MenuItemWC }>;

declare global {
  interface HTMLElementTagNameMap {
    'ps-menu-item': MenuItemWC;
  }
  enum PSElementTagNameMap {
    'ps-menu-item' = 'ps-menu-item',
  }

  interface GlobalEventHandlersEventMap {
    'menu-item-selected': MenuItemSelectedEvent;
  }
}
